import React, { useState, useEffect } from "react";
import {
  Paper,
  Text,
  Box,
  Button,
  Stack,
  Group,
  Container,
  TextInput,
  Select,
  ThemeIcon,
  Title,
  Badge,
  Divider,
  SimpleGrid,
  Card,
  Avatar,
  Progress,
  Grid,
  Image,
  Anchor,
} from "@mantine/core";
import {
  IconCash,
  IconPigMoney,
  IconChartLine,
  IconCreditCard,
  IconShieldCheck,
  IconCheck,
  IconX,
  IconUser,
  IconWallet,
  IconArrowsExchange,
  IconReceipt,
  IconBuildingBank,
  IconArrowRight,
  IconLock,
  IconDeviceMobile,
  IconWorld,
} from "@tabler/icons-react";
import { useGlobal } from "../../context/global-context";
import VerificationIframe from "../../components/verification-iframe";

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }, { value: "DC", label: "District of Columbia" },
];

// Mock dashboard data
const DASHBOARD_DATA = {
  balance: 12458.32,
  monthlyGrowth: 8.4,
  recentTransactions: [
    { name: "Direct Deposit", amount: 2500.0, type: "credit" },
    { name: "Amazon", amount: -89.99, type: "debit" },
    { name: "Transfer to Savings", amount: -500.0, type: "debit" },
    { name: "Dividend Payment", amount: 12.45, type: "credit" },
  ],
};

export default function Register() {
  const { fintechFlow, updateFintechFlow, wineStoreSettings } = useGlobal();

  const optionalFields = wineStoreSettings?.optionalFields || {};

  // Local UI state
  const [ageFailed, setAgeFailed] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [verificationIframeOpened, setVerificationIframeOpened] = useState(false);
  const [verificationLink, setVerificationLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [formError, setFormError] = useState("");
  const [apiError, setApiError] = useState(null);

  // Use flow state from context
  const flowStep = fintechFlow.step;
  const email = fintechFlow.email;
  const firstName = fintechFlow.firstName;
  const lastName = fintechFlow.lastName;
  const phone = fintechFlow.phone;
  const ssn4 = fintechFlow.ssn4;

  const setFlowStep = (step) => updateFintechFlow({ step });
  const setEmail = (val) => updateFintechFlow({ email: val });
  const setFirstName = (val) => updateFintechFlow({ firstName: val });
  const setLastName = (val) => updateFintechFlow({ lastName: val });
  const setPhone = (val) => updateFintechFlow({ phone: val });
  const setSsn4 = (val) => updateFintechFlow({ ssn4: val });

  // Extra form fields not in fintechFlow context
  const [formData, setFormData] = useState({
    dobMonth: "",
    dobDay: "",
    dobYear: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isEmailValid = email && /\S+@\S+\.\S+/.test(email);
  const isFormComplete =
    isEmailValid &&
    (!optionalFields.firstName || firstName) &&
    (!optionalFields.lastName || lastName) &&
    (!optionalFields.phone || phone) &&
    (!optionalFields.ssn4 || (ssn4 && ssn4.length === 4)) &&
    (!optionalFields.dob || (formData.dobMonth && formData.dobDay && formData.dobYear)) &&
    (!optionalFields.address || (formData.address && formData.city && formData.state && formData.zipCode));

  const handleSignupSubmit = () => {
    setFormError("");
    if (!isEmailValid) {
      setFormError("Please enter a valid email address");
      return;
    }
    if (optionalFields.firstName && !firstName) {
      setFormError("Please enter your first name");
      return;
    }
    if (optionalFields.lastName && !lastName) {
      setFormError("Please enter your last name");
      return;
    }
    if (optionalFields.phone && !phone) {
      setFormError("Please enter your phone number");
      return;
    }
    if (optionalFields.ssn4 && (!ssn4 || ssn4.length !== 4)) {
      setFormError("Please enter the last 4 digits of your SSN");
      return;
    }
    if (optionalFields.dob && (!formData.dobMonth || !formData.dobDay || !formData.dobYear)) {
      setFormError("Please enter your date of birth");
      return;
    }
    if (optionalFields.address && (!formData.address || !formData.city || !formData.state || !formData.zipCode)) {
      setFormError("Please enter your full address");
      return;
    }
    triggerVerification();
  };

  const triggerVerification = async () => {
    if (failureCount >= 2) {
      setAgeFailed(true);
      return;
    }

    setLoading(true);
    setLoadingMessage("Getting verification session...");
    setFlowStep("verifying");
    setApiError(null);

    const isProduction = window.location.hostname === "demo.dcams.app";
    const baseUrl = isProduction ? "https://be.demo.veratad.app" : "http://localhost:8080";

    try {
      const response = await fetch(
        `${baseUrl}/demo/vx/create-journey.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            ...(optionalFields.phone && phone ? { phone: phone.replace(/\D/g, "") } : {}),
            target: {
              ...(optionalFields.firstName && firstName ? { fn: firstName } : {}),
              ...(optionalFields.lastName && lastName ? { ln: lastName } : {}),
              ...(optionalFields.phone && phone ? { phone: phone.replace(/\D/g, "") } : {}),
              ...(optionalFields.ssn4 && ssn4 ? { ssn4: ssn4 } : {}),
              ...(optionalFields.dob && formData.dobYear ? { dob: `${formData.dobYear}${formData.dobMonth.padStart(2, "0")}${formData.dobDay.padStart(2, "0")}` } : {}),
              ...(optionalFields.address && formData.address ? { addr: formData.address, city: formData.city, state: formData.state, zip: formData.zipCode } : {}),
            },
            demo: "fintech",
            age: `${wineStoreSettings?.age ?? 21}+`,
            method: wineStoreSettings?.integrationMethod || "iframe",
            test_mode: wineStoreSettings?.testMode ?? true,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setApiError({
          error: errorData.error || "Error",
          message: errorData.message || "Something went wrong. Please try again.",
        });
        setFlowStep("signup");
        return;
      }

      const data = await response.json();

      if (data.link) {
        if ((wineStoreSettings?.integrationMethod || "iframe") === "redirect") {
          window.open(data.link, "_blank");
          setFlowStep("signup");
          return;
        }

        setVerificationLink(data.link);
        setVerificationIframeOpened(true);
      } else {
        throw new Error("No verification link received");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setApiError({
        error: "Network Error",
        message: "Could not connect to the verification service. Please try again.",
      });
      setFlowStep("signup");
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Handle redirect return - check for request_id in query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("request_id");
    if (!requestId) return;

    const checkResult = async () => {
      setLoading(true);
      setLoadingMessage("Checking verification result...");
      setApiError(null);

      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const baseUrl = isLocalhost ? "http://localhost:8080" : "https://be.demo.veratad.app";

      try {
        const response = await fetch(
          `${baseUrl}/demo/vx/get-result.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              request_id: requestId,
              demo: "fintech",
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setApiError({
            error: errorData.error || "Error",
            message: errorData.message || "Something went wrong. Please try again.",
          });
          setFlowStep("signup");
          return;
        }

        const data = await response.json();

        if (data.status === "PASS") {
          setFlowStep("verified");
          setAgeFailed(false);
        } else {
          setAgeFailed(true);
          setFailureCount((prev) => prev + 1);
          setFlowStep("signup");
        }
      } catch (error) {
        console.error("Get result error:", error);
        setApiError({
          error: "Network Error",
          message: "Could not connect to the verification service. Please try again.",
        });
        setFlowStep("signup");
      } finally {
        setLoading(false);
        setLoadingMessage("");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    checkResult();
  }, []); // Run once on mount

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "CLOSE") {
        setVerificationIframeOpened(false);
        setFlowStep("signup");
      }
      if (event.data === "PASS") {
        setVerificationIframeOpened(false);
        setFlowStep("verified");
        setAgeFailed(false);
      }
      if (event.data === "FAIL") {
        setVerificationIframeOpened(false);
        setAgeFailed(true);
        setFailureCount((prev) => prev + 1);
        setFlowStep("signup");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const isVerified = flowStep === "verified";

  return (
    <>
      {/* Landing State - Full page professional layout */}
      {flowStep === "landing" && (
        <Box style={{ backgroundColor: "#0a0a0a", minHeight: "100vh" }}>
          {/* Hero Section */}
          <Box
            style={{
              background: "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
              minHeight: "90vh",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Gradient orbs for visual interest */}
            <Box
              style={{
                position: "absolute",
                top: "-20%",
                right: "-10%",
                width: "600px",
                height: "600px",
                background: "radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
            <Box
              style={{
                position: "absolute",
                bottom: "-30%",
                left: "-10%",
                width: "500px",
                height: "500px",
                background: "radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />

            <Container size="xl" py={100} style={{ position: "relative", zIndex: 1 }}>
              <Grid gutter={80} align="center">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap="xl">
                    <Badge
                      size="lg"
                      variant="filled"
                      color="indigo"
                      leftSection={<IconWallet size={14} />}
                      style={{ width: "fit-content" }}
                    >
                      Next-Gen Banking
                    </Badge>

                    <Title
                      order={1}
                      style={{
                        fontSize: "4rem",
                        fontWeight: 800,
                        lineHeight: 1.05,
                        color: "#ffffff",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Banking that
                      <br />
                      <Text
                        span
                        inherit
                        style={{
                          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        works for you
                      </Text>
                    </Title>

                    <Text size="xl" c="gray.4" maw={480} lh={1.7} fw={400}>
                      Open a powerful bank account in minutes. Send, spend, save, and invest—all in one place with zero hidden fees.
                    </Text>

                    <Group mt="lg" gap="md">
                      <Button
                        size="xl"
                        variant="gradient"
                        gradient={{ from: "indigo", to: "violet", deg: 135 }}
                        rightSection={<IconArrowRight size={20} />}
                        onClick={() => setFlowStep("signup")}
                        style={{ fontWeight: 600 }}
                      >
                        Get Started Free
                      </Button>
                    </Group>

                    <Group mt="xl" gap="xl">
                      <Stack gap={2}>
                        <Text size="xl" fw={700} c="white">$0</Text>
                        <Text size="sm" c="gray.5">Monthly fees</Text>
                      </Stack>
                      <Stack gap={2}>
                        <Text size="xl" fw={700} c="white">4.5%</Text>
                        <Text size="sm" c="gray.5">APY savings</Text>
                      </Stack>
                      <Stack gap={2}>
                        <Text size="xl" fw={700} c="white">2M+</Text>
                        <Text size="sm" c="gray.5">Happy users</Text>
                      </Stack>
                    </Group>
                  </Stack>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Box
                    style={{
                      position: "relative",
                      height: "700px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "visible",
                    }}
                  >
                    <Image
                      src="/fintech_hero.png"
                      alt="NovaPay Card"
                      fit="contain"
                      radius="xl"
                      style={{
                        transform: "scale(1.6)",
                        borderRadius: "24px",
                      }}
                    />
                  </Box>
                </Grid.Col>
              </Grid>
            </Container>
          </Box>

          {/* Features Section */}
          <Box py={100} style={{ backgroundColor: "#0a0a0a" }}>
            <Container size="xl">
              <Stack gap={70}>
                <div style={{ textAlign: "center" }}>
                  <Badge size="lg" variant="filled" color="violet" mb="lg">
                    FEATURES
                  </Badge>
                  <Title order={2} c="white" mb="md" style={{ fontSize: "2.75rem", fontWeight: 700 }}>
                    Everything you need in one place
                  </Title>
                  <Text c="gray.5" size="xl" maw={600} mx="auto">
                    A complete financial toolkit built for the modern world.
                  </Text>
                </div>

                <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="xl">
                  <Card
                    padding="xl"
                    radius="lg"
                    style={{
                      backgroundColor: "#111118",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ThemeIcon size={60} radius="lg" variant="gradient" gradient={{ from: "blue", to: "cyan" }} mb="lg">
                      <IconArrowsExchange size={30} />
                    </ThemeIcon>
                    <Text fw={700} size="xl" c="white" mb="sm">Instant Transfers</Text>
                    <Text size="md" c="gray.5" lh={1.6}>
                      Send money to anyone instantly. No waiting, no fees, no limits.
                    </Text>
                  </Card>

                  <Card
                    padding="xl"
                    radius="lg"
                    style={{
                      backgroundColor: "#111118",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ThemeIcon size={60} radius="lg" variant="gradient" gradient={{ from: "green", to: "teal" }} mb="lg">
                      <IconPigMoney size={30} />
                    </ThemeIcon>
                    <Text fw={700} size="xl" c="white" mb="sm">High-Yield Savings</Text>
                    <Text size="md" c="gray.5" lh={1.6}>
                      Earn 4.5% APY on your savings with no minimum balance required.
                    </Text>
                  </Card>

                  <Card
                    padding="xl"
                    radius="lg"
                    style={{
                      backgroundColor: "#111118",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ThemeIcon size={60} radius="lg" variant="gradient" gradient={{ from: "violet", to: "grape" }} mb="lg">
                      <IconChartLine size={30} />
                    </ThemeIcon>
                    <Text fw={700} size="xl" c="white" mb="sm">Smart Investing</Text>
                    <Text size="md" c="gray.5" lh={1.6}>
                      Stocks, ETFs, and crypto—all in one powerful platform.
                    </Text>
                  </Card>

                  <Card
                    padding="xl"
                    radius="lg"
                    style={{
                      backgroundColor: "#111118",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ThemeIcon size={60} radius="lg" variant="gradient" gradient={{ from: "orange", to: "red" }} mb="lg">
                      <IconCreditCard size={30} />
                    </ThemeIcon>
                    <Text fw={700} size="xl" c="white" mb="sm">Premium Cards</Text>
                    <Text size="md" c="gray.5" lh={1.6}>
                      Virtual and physical cards with unlimited cashback rewards.
                    </Text>
                  </Card>
                </SimpleGrid>
              </Stack>
            </Container>
          </Box>

          {/* Trust Section */}
          <Box py={100} style={{ backgroundColor: "#111118" }}>
            <Container size="xl">
              <Grid gutter={80} align="center">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Image
                      src="/fintech_security.png"
                      alt="Security"
                      fit="contain"
                      radius="xl"
                      style={{
                        maxHeight: "550px",
                        borderRadius: "24px",
                        transform: "scale(1.15)",
                      }}
                    />
                  </Box>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap="xl">
                    <Badge
                      size="lg"
                      variant="filled"
                      color="green"
                      leftSection={<IconLock size={14} />}
                      style={{ width: "fit-content" }}
                    >
                      Bank-Grade Security
                    </Badge>

                    <Title order={2} c="white" style={{ fontSize: "2.75rem", lineHeight: 1.15, fontWeight: 700 }}>
                      Your money is protected 24/7
                    </Title>

                    <Text size="xl" c="gray.4" lh={1.7}>
                      We use the same security infrastructure trusted by the world's largest financial institutions.
                    </Text>

                    <Stack gap="lg" mt="md">
                      <Group gap="lg">
                        <ThemeIcon size={56} radius="lg" variant="gradient" gradient={{ from: "indigo", to: "violet" }}>
                          <IconLock size={28} />
                        </ThemeIcon>
                        <div>
                          <Text fw={700} size="lg" c="white">256-bit Encryption</Text>
                          <Text size="md" c="gray.5">Military-grade data protection</Text>
                        </div>
                      </Group>

                      <Group gap="lg">
                        <ThemeIcon size={56} radius="lg" variant="gradient" gradient={{ from: "green", to: "teal" }}>
                          <IconShieldCheck size={28} />
                        </ThemeIcon>
                        <div>
                          <Text fw={700} size="lg" c="white">FDIC Insured</Text>
                          <Text size="md" c="gray.5">Up to $250,000 protected</Text>
                        </div>
                      </Group>

                      <Group gap="lg">
                        <ThemeIcon size={56} radius="lg" variant="gradient" gradient={{ from: "orange", to: "red" }}>
                          <IconDeviceMobile size={28} />
                        </ThemeIcon>
                        <div>
                          <Text fw={700} size="lg" c="white">Biometric Auth</Text>
                          <Text size="md" c="gray.5">Face ID & fingerprint support</Text>
                        </div>
                      </Group>
                    </Stack>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Container>
          </Box>

          {/* CTA Section */}
          <Box
            py={120}
            style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
            }}
          >
            <Container size="md" style={{ textAlign: "center" }}>
              <Stack gap="xl" align="center">
                <Title order={2} c="white" style={{ fontSize: "3rem", fontWeight: 700 }}>
                  Ready to take control?
                </Title>
                <Text size="xl" c="white" opacity={0.9} maw={500}>
                  Join over 2 million people who've made the switch to smarter banking.
                </Text>
                <Button
                  size="xl"
                  color="white"
                  variant="white"
                  c="dark"
                  rightSection={<IconArrowRight size={22} />}
                  onClick={() => setFlowStep("signup")}
                  style={{ fontWeight: 700, fontSize: "1.1rem" }}
                >
                  Open Your Free Account
                </Button>
                <Group gap="xl" mt="md">
                  <Text size="sm" c="white" opacity={0.8}>
                    <IconCheck size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    No credit check
                  </Text>
                  <Text size="sm" c="white" opacity={0.8}>
                    <IconCheck size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    FDIC insured
                  </Text>
                  <Text size="sm" c="white" opacity={0.8}>
                    <IconCheck size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
                    Cancel anytime
                  </Text>
                </Group>
              </Stack>
            </Container>
          </Box>
        </Box>
      )}

      {/* Non-landing states use container */}
      {flowStep !== "landing" && (
        <Box style={{ backgroundColor: "#0a0a0a", minHeight: "100vh" }}>
          <Container size="sm" py={80}>

            {/* Signup State */}
          {flowStep === "signup" && (
            <Box>
              <Stack gap="xl" align="center" mb={40}>
                <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: "indigo", to: "violet" }}>
                  <IconUser size={40} />
                </ThemeIcon>
                <div style={{ textAlign: "center" }}>
                  <Title order={1} c="white" mb="xs" style={{ fontSize: "2.5rem", fontWeight: 700 }}>
                    Create Your Account
                  </Title>
                  <Text size="lg" c="gray.5">
                    Get started with NovaPay in minutes
                  </Text>
                </div>
              </Stack>

              <Paper
                p="xl"
                radius="lg"
                style={{
                  backgroundColor: "#111118",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Stack gap="lg">
                  <TextInput
                    label="Email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isVerified}
                    required
                    size="md"
                    styles={{
                      label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                      input: {
                        backgroundColor: "#1a1a24",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "white",
                      },
                    }}
                  />

                  {(optionalFields.firstName || optionalFields.lastName) && (
                    <Group grow>
                      {optionalFields.firstName && (
                        <TextInput
                          label="First Name"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          disabled={isVerified}
                          required
                          size="md"
                          styles={{
                            label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                            input: {
                              backgroundColor: "#1a1a24",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "white",
                            },
                          }}
                        />
                      )}
                      {optionalFields.lastName && (
                        <TextInput
                          label="Last Name"
                          placeholder="Doe"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          disabled={isVerified}
                          required
                          size="md"
                          styles={{
                            label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                            input: {
                              backgroundColor: "#1a1a24",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "white",
                            },
                          }}
                        />
                      )}
                    </Group>
                  )}

                  {optionalFields.phone && (
                    <TextInput
                      label="Phone Number"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^\d()-\s]/g, ""))}
                      disabled={isVerified}
                      required
                      size="md"
                      styles={{
                        label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                        input: {
                          backgroundColor: "#1a1a24",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "white",
                        },
                      }}
                    />
                  )}

                  {optionalFields.dob && (
                    <Group grow>
                      <Select
                        label="Month"
                        placeholder="Month"
                        data={[
                          { value: "1", label: "January" }, { value: "2", label: "February" },
                          { value: "3", label: "March" }, { value: "4", label: "April" },
                          { value: "5", label: "May" }, { value: "6", label: "June" },
                          { value: "7", label: "July" }, { value: "8", label: "August" },
                          { value: "9", label: "September" }, { value: "10", label: "October" },
                          { value: "11", label: "November" }, { value: "12", label: "December" },
                        ]}
                        value={formData.dobMonth}
                        onChange={(value) => handleFormChange("dobMonth", value)}
                        disabled={isVerified}
                        required
                        size="md"
                        styles={{
                          label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                          input: {
                            backgroundColor: "#1a1a24",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "white",
                          },
                        }}
                      />
                      <Select
                        label="Day"
                        placeholder="Day"
                        data={Array.from({ length: 31 }, (_, i) => ({
                          value: String(i + 1),
                          label: String(i + 1),
                        }))}
                        value={formData.dobDay}
                        onChange={(value) => handleFormChange("dobDay", value)}
                        disabled={isVerified}
                        required
                        size="md"
                        styles={{
                          label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                          input: {
                            backgroundColor: "#1a1a24",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "white",
                          },
                        }}
                      />
                      <Select
                        label="Year"
                        placeholder="Year"
                        data={Array.from({ length: 100 }, (_, i) => {
                          const year = new Date().getFullYear() - 18 - i;
                          return { value: String(year), label: String(year) };
                        })}
                        value={formData.dobYear}
                        onChange={(value) => handleFormChange("dobYear", value)}
                        disabled={isVerified}
                        required
                        searchable
                        size="md"
                        styles={{
                          label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                          input: {
                            backgroundColor: "#1a1a24",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "white",
                          },
                        }}
                      />
                    </Group>
                  )}

                  {optionalFields.ssn4 && (
                    <TextInput
                      label="Last 4 of SSN"
                      placeholder="1234"
                      description="Required for identity verification"
                      value={ssn4}
                      onChange={(e) => setSsn4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      maxLength={4}
                      disabled={isVerified}
                      required
                      size="md"
                      styles={{
                        label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                        description: { color: "#71717a", marginTop: 4 },
                        input: {
                          backgroundColor: "#1a1a24",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "white",
                        },
                      }}
                    />
                  )}

                  {optionalFields.address && (
                    <>
                      <TextInput
                        label="Address"
                        placeholder="123 Main St"
                        value={formData.address}
                        onChange={(e) => handleFormChange("address", e.target.value)}
                        disabled={isVerified}
                        required
                        size="md"
                        styles={{
                          label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                          input: {
                            backgroundColor: "#1a1a24",
                            border: "1px solid rgba(255,255,255,0.1)",
                            color: "white",
                          },
                        }}
                      />
                      <Group grow>
                        <TextInput
                          label="City"
                          placeholder="New York"
                          value={formData.city}
                          onChange={(e) => handleFormChange("city", e.target.value)}
                          disabled={isVerified}
                          required
                          size="md"
                          styles={{
                            label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                            input: {
                              backgroundColor: "#1a1a24",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "white",
                            },
                          }}
                        />
                        <Select
                          label="State"
                          placeholder="Select state"
                          data={US_STATES}
                          value={formData.state}
                          onChange={(value) => handleFormChange("state", value)}
                          disabled={isVerified}
                          required
                          size="md"
                          styles={{
                            label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                            input: {
                              backgroundColor: "#1a1a24",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "white",
                            },
                          }}
                        />
                        <TextInput
                          label="ZIP Code"
                          placeholder="10001"
                          value={formData.zipCode}
                          onChange={(e) => handleFormChange("zipCode", e.target.value)}
                          disabled={isVerified}
                          required
                          size="md"
                          styles={{
                            label: { color: "#a1a1aa", marginBottom: 8, fontWeight: 500 },
                            input: {
                              backgroundColor: "#1a1a24",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "white",
                            },
                          }}
                        />
                      </Group>
                    </>
                  )}

                  {formError && (
                    <Text size="sm" c="red.4">{formError}</Text>
                  )}

                  {ageFailed && (
                    <Paper p="sm" radius="md" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
                      <Group>
                        <ThemeIcon color="red" variant="light" size="sm">
                          <IconX size={14} />
                        </ThemeIcon>
                        <Text size="sm" c="red.4">
                          {failureCount >= 2
                            ? "Maximum attempts reached - Please contact support"
                            : "Verification failed - Please try again"}
                        </Text>
                      </Group>
                    </Paper>
                  )}

                  {apiError && (
                    <Paper p="sm" radius="md" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
                      <Group>
                        <ThemeIcon color="red" variant="light" size="sm">
                          <IconX size={14} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={600} c="red.4">{apiError.error}</Text>
                          <Text size="sm" c="red.4">{apiError.message}</Text>
                        </div>
                      </Group>
                    </Paper>
                  )}

                  <Button
                    fullWidth
                    size="lg"
                    onClick={(!isFormComplete || failureCount >= 2) ? undefined : handleSignupSubmit}
                    loading={loading}
                    loaderProps={{ type: "dots" }}
                    mt="md"
                    style={{
                      fontWeight: 600,
                      backgroundImage: "linear-gradient(135deg, #4338ca 0%, #7c3aed 100%)",
                      color: "white",
                      opacity: (!isFormComplete || failureCount >= 2) ? 0.45 : 1,
                      cursor: (!isFormComplete || failureCount >= 2) ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading
                      ? loadingMessage
                      : failureCount >= 2
                        ? "Verification Unavailable"
                        : ageFailed
                          ? "Try Again"
                          : "Continue"}
                  </Button>

                  <Group justify="center" gap="xs" mt="sm">
                    <IconLock size={14} color="#71717a" />
                    <Text size="xs" c="gray.6" ta="center">
                      Your information is encrypted and secure
                    </Text>
                  </Group>
                </Stack>
              </Paper>

              <Text size="xs" c="gray.6" ta="center" mt="xl">
                By continuing, you agree to our{" "}
                <Anchor c="indigo.4" size="xs">Terms of Service</Anchor>
                {" "}and{" "}
                <Anchor c="indigo.4" size="xs">Privacy Policy</Anchor>
              </Text>
            </Box>
          )}

          {/* Verifying State */}
          {flowStep === "verifying" && !verificationIframeOpened && (
            <Box>
              <Stack gap="xl" align="center">
                <ThemeIcon size={100} radius="xl" variant="gradient" gradient={{ from: "indigo", to: "violet" }}>
                  <IconShieldCheck size={50} />
                </ThemeIcon>
                <Title order={2} c="white" ta="center">Verifying Your Identity</Title>
                <Text c="gray.5" size="lg">{loadingMessage || "Please wait..."}</Text>
                <Progress value={100} animated size="md" w="100%" color="indigo" radius="xl" style={{ maxWidth: 400 }} />
              </Stack>
            </Box>
          )}

          {/* Verified State - Dashboard */}
          {isVerified && (
            <>
              {/* Welcome Banner */}
              <Paper
                p="xl"
                radius="lg"
                mb="xl"
                style={{
                  background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs">
                    <Group gap="sm">
                      <ThemeIcon size="lg" radius="xl" color="white" variant="white">
                        <IconCheck size={20} color="#6366f1" />
                      </ThemeIcon>
                      <Badge color="white" variant="white" size="lg" style={{ color: "#6366f1" }}>
                        Identity Verified
                      </Badge>
                    </Group>
                    <Title order={2} c="white">Welcome to NovaPay, {firstName}!</Title>
                    <Text c="white" opacity={0.9}>
                      Your account is ready. Start exploring all the features.
                    </Text>
                  </Stack>
                  <Avatar color="white" radius="xl" size="xl" style={{ border: "3px solid white" }}>
                    {firstName ? firstName.charAt(0).toUpperCase() : "U"}
                  </Avatar>
                </Group>
              </Paper>

              {/* Balance Card */}
              <Paper
                p="xl"
                radius="lg"
                mb="lg"
                style={{
                  backgroundColor: "#111118",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Group justify="space-between" mb="md">
                  <div>
                    <Text size="sm" c="gray.5">Total Balance</Text>
                    <Title order={1} c="white">${DASHBOARD_DATA.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Title>
                  </div>
                  <Badge color="green" size="lg" leftSection={<IconChartLine size={14} />}>
                    +{DASHBOARD_DATA.monthlyGrowth}% this month
                  </Badge>
                </Group>
                <Group grow>
                  <Button variant="gradient" gradient={{ from: "indigo", to: "violet" }} leftSection={<IconArrowsExchange size={18} />}>
                    Transfer
                  </Button>
                  <Button variant="light" color="indigo" leftSection={<IconBuildingBank size={18} />}>
                    Add Money
                  </Button>
                  <Button variant="light" color="violet" leftSection={<IconReceipt size={18} />}>
                    Pay Bills
                  </Button>
                </Group>
              </Paper>

              {/* Recent Transactions */}
              <Paper
                p="lg"
                radius="lg"
                style={{
                  backgroundColor: "#111118",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Text fw={600} mb="md" c="white">Recent Transactions</Text>
                <Stack gap="sm">
                  {DASHBOARD_DATA.recentTransactions.map((tx, index) => (
                    <Group
                      key={index}
                      justify="space-between"
                      p="sm"
                      style={{
                        borderBottom: index < DASHBOARD_DATA.recentTransactions.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none"
                      }}
                    >
                      <Group>
                        <ThemeIcon
                          size="md"
                          radius="xl"
                          color={tx.type === "credit" ? "green" : "gray"}
                          variant="light"
                        >
                          {tx.type === "credit" ? <IconCash size={16} /> : <IconCreditCard size={16} />}
                        </ThemeIcon>
                        <Text size="sm" c="gray.3">{tx.name}</Text>
                      </Group>
                      <Text
                        fw={600}
                        c={tx.type === "credit" ? "green.4" : "gray.4"}
                      >
                        {tx.type === "credit" ? "+" : ""}{tx.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Paper>
            </>
          )}
          </Container>
        </Box>
      )}

      <VerificationIframe
        opened={verificationIframeOpened}
        onClose={() => {
          setVerificationIframeOpened(false);
          setFlowStep("signup");
        }}
        verificationLink={verificationLink}
        title="Identity Verification"
      />
    </>
  );
}
